import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BaseUser,
  getDefaultLanguage,
  LanguageService,
} from '@awo-care/shared-core';
import {
  LastLoginInfo,
  LAST_LOGIN_KEY,
  buildLastLoginInfo,
} from './last-login.utils';
import { Auth, AuthProvider, EmailAuthProvider, User, applyActionCode, confirmPasswordReset, createUserWithEmailAndPassword, reauthenticateWithCredential, signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, verifyBeforeUpdateEmail } from 'firebase/auth';
import { Functions, httpsCallable } from 'firebase/functions';
import { Firestore, Timestamp, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE, FIREBASE_FUNCTIONS } from '@awo-care/shared-firebase';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseAuthService {
  protected auth = inject(FIREBASE_AUTH);
  protected firestore = inject(FIREBASE_FIRESTORE);
  protected functions = inject(FIREBASE_FUNCTIONS);
  protected router = inject(Router);
  protected languageService = inject(LanguageService, { optional: true });

  private attemptCount = new Map<
    string,
    { count: number; timestamp: number }
  >();
  protected abstract userCollection: string;

  protected createBaseUserData(user: User): BaseUser {
    return {
      id: user.uid,
      email: user.email || user.phoneNumber || '',
      emailVerified: user.emailVerified,
      isActive: true,
      provider: user.providerData[0]?.providerId || 'unknown',
      role: 'customer',
      lastSignIn: serverTimestamp() as unknown as Timestamp,
      createdOn: serverTimestamp() as unknown as Timestamp,
      language: this.languageService?.currentLanguage()?.code ?? getDefaultLanguage().code,
      // Raw browser locale for distribution analysis (SSR-safe guard)
      browserLocale: typeof navigator !== 'undefined' ? navigator.language : '',
    };
  }

  protected async validateToken(token: string): Promise<boolean> {
    try {
      const user = this.auth.currentUser;
      if (!user) return false;
      const newToken = await user.getIdToken(true); // Force refresh
      return token === newToken;
    } catch {
      return false;
    }
  }

  protected async refreshToken(): Promise<string | null> {
    try {
      const user = this.auth.currentUser;
      if (!user) return null;
      return await user.getIdToken(true);
    } catch (error) {
      console.error('Token refresh failed:', error);
      await this.signOut();
      return null;
    }
  }

  protected checkRateLimit(email: string): boolean {
    const now = Date.now();
    const attempt = this.attemptCount.get(email);

    if (attempt && now - attempt.timestamp < 3600000) {
      if (attempt.count >= 5) return false;
      attempt.count++;
    } else {
      this.attemptCount.set(email, { count: 1, timestamp: now });
    }
    return true;
  }

  protected async loginWithProvider(provider: AuthProvider): Promise<User> {
    try {
      const result = await signInWithPopup(this.auth, provider);
      if (!result.user) throw new Error('No user data returned');
      await this.processLogin(result.user);
      return result.user;
    } catch (error: unknown) {
      const authError = error as { code?: string };
      switch (authError.code) {
        case 'auth/popup-blocked':
          // Fallback to redirect-based auth when popup is blocked
          await signInWithRedirect(this.auth, provider);
          return null as unknown as User;
        case 'auth/cancelled-popup-request':
          throw new Error('Login cancelled.');
        case 'auth/account-exists-with-different-credential':
          throw new Error('Account exists with different login method.');
        default:
          throw error;
      }
    }
  }

  protected async processLogin(user: User): Promise<void> {
    if (!user?.uid || !user.email) {
      throw new Error('Invalid user data');
    }

    const userData = this.createBaseUserData(user);
    await this.addFirebaseUser(user.uid, userData);
    this.saveLastLogin(user);
  }

  /**
   * "Weiter als …"-Login-Hinweis (Issue #634): merkt sich die zuletzt genutzte
   * Anmeldemethode, damit die Login-Seite sie beim Wiederkommen anbieten kann.
   * Verhindert versehentliche Doppel-Accounts (Apple vs. E-Mail). Alle
   * localStorage-Zugriffe defensiv gekapselt.
   */
  protected saveLastLogin(user: User): void {
    try {
      const info = buildLastLoginInfo(user);
      localStorage.setItem(LAST_LOGIN_KEY, JSON.stringify(info));
    } catch (error) {
      console.error('Failed to save last login info:', error);
    }
  }

  getLastLogin(): LastLoginInfo | null {
    try {
      const raw = localStorage.getItem(LAST_LOGIN_KEY);
      return raw ? (JSON.parse(raw) as LastLoginInfo) : null;
    } catch {
      return null;
    }
  }

  clearLastLogin(): void {
    try {
      localStorage.removeItem(LAST_LOGIN_KEY);
    } catch (error) {
      console.error('Failed to clear last login info:', error);
    }
  }

  protected async getUserData<T extends BaseUser>(
    uid: string
  ): Promise<T | null> {
    const userRef = doc(this.firestore, this.userCollection, uid);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? (userDoc.data() as T) : null;
  }

  protected async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  }

  protected async addFirebaseUser<T extends BaseUser>(
    uid: string,
    userData: T
  ): Promise<T> {
    if (!uid) {
      throw new Error('Cannot save user document: missing UID');
    }
    try {
      const userRef = doc(this.firestore, this.userCollection, uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, userData);
        // Return with real timestamps (userData has serverTimestamp sentinels)
        return { ...userData, lastSignIn: Timestamp.now(), createdOn: Timestamp.now() };
      }

      const existingData = userDoc.data();
      await updateDoc(userRef, {
        lastSignIn: serverTimestamp(),
        // Sync email from Auth — source of truth
        email: userData.email,
        emailVerified: userData.emailVerified,
        // Backfill/refresh raw browser locale on every login
        ...(userData.browserLocale && { browserLocale: userData.browserLocale }),
        // Merge phone data if not already stored
        ...(userData.phoneNumber &&
          !existingData['phoneNumber'] && {
            phoneNumber: userData.phoneNumber,
            phoneVerified: userData.phoneVerified,
          }),
      });
      return { ...existingData, ...userData, lastSignIn: Timestamp.now() } as T;
    } catch (error) {
      const err = error as Error;
      throw new Error(`Failed to add/update user: ${err.message}`);
    }
  }

  async reauthenticate(email: string, password: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No authenticated user');
    // Trim, damit von Mobil-Tastaturen angehängte Leerzeichen nicht zu
    // auth/invalid-email führen (siehe sendPasswordReset).
    const normalizedEmail = email?.trim() ?? '';
    if (!normalizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      throw new Error('Invalid email format');
    }
    if (!password || password.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }
    const credential = EmailAuthProvider.credential(normalizedEmail, password);
    await reauthenticateWithCredential(user, credential);
  }

  async emailLogin(email: string, password: string): Promise<User> {
    const normalizedEmail = email?.trim() ?? '';
    if (!normalizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      throw new Error('Invalid email format');
    }
    if (!password || password.length < 8) {
      throw new Error('Password must be at least 8 characters');
    }
    if (!this.checkRateLimit(normalizedEmail)) {
      throw new Error('Too many login attempts. Try again later.');
    }

    const userCredential = await signInWithEmailAndPassword(
      this.auth,
      normalizedEmail,
      password
    );
    await this.processLogin(userCredential.user);
    return userCredential.user;
  }

  async updateUser(uid: string, data: Record<string, unknown>): Promise<void> {
    try {
      const userRef = doc(this.firestore, this.userCollection, uid);
      await updateDoc(userRef, data);
    } catch (error) {
      const err = error as Error;
      throw new Error(`Failed to update user: ${err.message}`);
    }
  }

  async sendPasswordReset(email: string) {
    try {
      // Use Cloud Function for secure password reset with hardcoded URL
      // Function uses SendGrid with custom email templates and async delivery
      // Eigener Client-Timeout knapp über dem Function-Backstop (40s, s.
      // functions/src/_shared/timeout-budgets.ts) statt dem httpsCallable-
      // Default von 70s (#970) — sonst wartet der Nutzer im Fehlerfall auf
      // eine Antwort, die die Function längst selbst abgebrochen hat.
      const sendPasswordResetFn = httpsCallable<
        { email: string },
        { success: boolean; message?: string; error?: string }
      >(this.functions, 'sendPasswordResetEmail', { timeout: 45_000 });

      const result = await sendPasswordResetFn({ email: email?.trim() ?? '' });

      if (!result.data.success) {
        // Throw the error message directly from the function
        throw new Error(result.data.error || 'Failed to send password reset email');
      }

      return true;
    } catch (error) {
      // Just re-throw the error as-is
      throw error;
    }
  }

  async createEmailAccount(email: string, password: string) {
    const normalizedEmail = email?.trim() ?? '';
    if (!this.checkRateLimit(normalizedEmail)) {
      throw new Error('Too many attempts. Try again later.');
    }
    return await createUserWithEmailAndPassword(
      this.auth,
      normalizedEmail,
      password
    );
  }

  abstract signOut(): Promise<void>;

  async sendVerificationEmail(user: User): Promise<void> {
    if (!user) throw new Error('No user provided');

    try {
      // Get current language from path or service
      const currentPath = window.location.pathname;
      const langMatch = currentPath.match(/^\/(de|en)\//);
      const language = langMatch ? langMatch[1] : this.languageService?.currentLanguage()?.code || 'de';

      // Use Cloud Function for secure verification email with hardcoded URL.
      // Eigener Client-Timeout knapp über dem Function-Backstop (40s, s.
      // functions/src/_shared/timeout-budgets.ts) statt dem httpsCallable-
      // Default von 70s (#970).
      const sendVerificationEmailFn = httpsCallable<
        { email: string; language: string },
        { success: boolean; message?: string; error?: string }
      >(this.functions, 'sendVerificationEmail', { timeout: 45_000 });

      const result = await sendVerificationEmailFn({
        email: user.email || '',
        language
      });

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to send verification email');
      }

      return;
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  }

  async handleVerifyEmail(actionCode: string): Promise<void> {
    try {
      // Apply the action code to verify the email
      // This works even if the user isn't logged in on this device
      await applyActionCode(this.auth, actionCode);

      if (!this.auth.currentUser) {
        return;
      }

      // After an email change, Firebase invalidates existing ID tokens, so
      // reload() may throw auth/user-token-expired. The email change itself
      // is already committed by applyActionCode — sign the user out so the
      // caller can route them to login, but do not surface this as an error.
      try {
        await this.auth.currentUser.reload();
      } catch (reloadError: unknown) {
        const err = reloadError as { code?: string };
        if (err.code === 'auth/user-token-expired') {
          await this.auth.signOut();
          return;
        }
        throw reloadError;
      }

      const updateData: Record<string, unknown> = { emailVerified: true };
      if (this.auth.currentUser.email) {
        updateData['email'] = this.auth.currentUser.email;
      }
      await this.updateUser(this.auth.currentUser.uid, updateData);
    } catch (error) {
      await this.router.navigateByUrl('auth/login');
      throw error;
    }
  }

  async handlePasswordReset(
    actionCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      return await confirmPasswordReset(this.auth, actionCode, newPassword);
    } catch (error) {
      const err = error as Error;
      throw new Error(`Failed to reset password: ${err.message}`);
    }
  }

  // Add this method to BaseAuthService
  async changeEmail(newEmail: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      const normalizedEmail = newEmail?.trim() ?? '';
      if (!normalizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error('Invalid email format');
      }

      if (!this.checkRateLimit(normalizedEmail)) {
        throw new Error('Too many attempts. Try again later.');
      }

      // Update email in Firebase Auth
      await verifyBeforeUpdateEmail(user, normalizedEmail, {
        url: `${window.location.origin}/auth/verify`,
        handleCodeInApp: true,
      });

      return;
    } catch (error) {
      console.error('Email change failed:', error);
      throw error;
    }
  }
}
